/**
 * Telemetria de conexão da sala — diagnóstico de quedas.
 *
 * Grava um evento em `connectionDiagnostics` a cada transição relevante
 * (reconectando, reconectado, roomLeft, reconexão suave, retorno de segundo
 * plano). O objetivo é entender POR QUE um usuário caiu: o servidor não
 * enxerga o `roomLeft`/estado de rede do cliente, então capturamos aqui.
 *
 * Regras de ouro:
 * - Nunca lança exceção (telemetria não pode quebrar a sala).
 * - Best-effort: se a escrita falhar (ex.: regra/offline), apenas ignora.
 */

import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";

export type ConnectionEventType =
    | "reconnecting"
    | "reconnected"
    | "roomLeft"
    | "softReconnectStart"
    | "softReconnectSuccess"
    | "softReconnectFail"
    | "reloadFallback"
    | "visibilityResume"
    | "visibilityHidden";

// Rastreia transições de visibilidade para detectar "voltou do segundo plano".
let lastHiddenAt: number | null = null;
let backgroundReturnAt: number | null = null;
let visibilityTrackerInstalled = false;

/** Marca retorno do segundo plano (chamado pelo Room ao detectar visibility). */
export function markBackgroundReturn(): void {
    backgroundReturnAt = Date.now();
}

function ensureVisibilityTracker(): void {
    if (visibilityTrackerInstalled || typeof document === "undefined") return;
    visibilityTrackerInstalled = true;
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
            lastHiddenAt = Date.now();
        } else if (document.visibilityState === "visible" && lastHiddenAt != null) {
            backgroundReturnAt = Date.now();
        }
    });
}

/** Evita gravar o mesmo evento 2× (ex.: dois DyteMeet montados). */
const recentEventKeys = new Map<string, number>();
const DEDUP_WINDOW_MS = 2500;

function shouldSkipDuplicate(type: string, ctx: ConnectionEventContext): boolean {
    const key = [
        type,
        ctx.roomId,
        ctx.userId ?? "",
        ctx.roomLeftState ?? "",
        ctx.reconnectAttempt ?? "",
    ].join("|");
    const now = Date.now();
    const prev = recentEventKeys.get(key);
    if (prev != null && now - prev < DEDUP_WINDOW_MS) {
        return true;
    }
    recentEventKeys.set(key, now);
    // Limpa entradas antigas ocasionalmente.
    if (recentEventKeys.size > 80) {
        for (const k of Array.from(recentEventKeys.keys())) {
            const t = recentEventKeys.get(k)!;
            if (now - t > DEDUP_WINDOW_MS * 4) {
                recentEventKeys.delete(k);
            }
        }
    }
    return false;
}

function getNetworkInfo(): Record<string, unknown> | null {
    if (typeof navigator === "undefined") return null;
    const connection = (navigator as unknown as {
        connection?: {
            effectiveType?: string;
            downlink?: number;
            rtt?: number;
            saveData?: boolean;
            type?: string;
        };
    }).connection;
    if (!connection) return null;
    return {
        effectiveType: connection.effectiveType ?? null,
        downlink: typeof connection.downlink === "number" ? connection.downlink : null,
        rtt: typeof connection.rtt === "number" ? connection.rtt : null,
        saveData: connection.saveData ?? null,
        type: connection.type ?? null,
    };
}

export interface ConnectionEventContext {
    roomId: string;
    userId?: string | null;
    role?: string | null;
    device?: string | null;
    roomLeftState?: string | null;
    socketState?: string | null;
    mediaState?: string | null;
    reconnectAttempt?: number | null;
}

export function logConnectionEvent(
    type: ConnectionEventType,
    ctx: ConnectionEventContext,
): void {
    try {
        ensureVisibilityTracker();

        if (shouldSkipDuplicate(type, ctx)) {
            return;
        }

        const now = Date.now();
        const visibilityState =
            typeof document !== "undefined" ? document.visibilityState : null;

        // "Veio do segundo plano" se retornou nos últimos 30s (janela curta pós-volta).
        const cameFromBackground =
            backgroundReturnAt != null && now - backgroundReturnAt < 30_000;
        const msSinceHidden = lastHiddenAt != null ? now - lastHiddenAt : null;
        const msSinceBackgroundReturn =
            backgroundReturnAt != null ? now - backgroundReturnAt : null;

        void addDoc(collection(db, "connectionDiagnostics"), {
            type,
            roomId: ctx.roomId,
            userId: ctx.userId ?? null,
            role: ctx.role ?? null,
            device: ctx.device ?? null,
            roomLeftState: ctx.roomLeftState ?? null,
            socketState: ctx.socketState ?? null,
            mediaState: ctx.mediaState ?? null,
            reconnectAttempt: ctx.reconnectAttempt ?? null,
            visibilityState,
            cameFromBackground,
            msSinceLastHidden: msSinceHidden,
            msSinceBackgroundReturn,
            online: typeof navigator !== "undefined" ? navigator.onLine : null,
            network: getNetworkInfo(),
            userAgent:
                typeof navigator !== "undefined"
                    ? navigator.userAgent.slice(0, 256)
                    : null,
            clientTs: now,
            ts: serverTimestamp(),
        }).catch(() => {});
    } catch {
        // Telemetria é reforço: nunca deve quebrar a sala.
    }
}
